import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { motion } from 'motion/react';
import { useContext } from 'react';

import { EditCardImageButton } from '../EditCardImageButton';
import type { ImageType, PlaceholderImageSize } from '../types';
import styles from './RightPicture.module.scss';
import { TemplatePlaceholder } from './TemplatePlaceholder';
import type { CardImage } from '.cache/__types__';

interface RightPictureProps {
  alt: string;
  canLock: boolean;
  disable?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onClickWhenDisabled?: React.MouseEventHandler<HTMLButtonElement>;
  quality: 'high' | 'low' | undefined;
  type: ImageType;
  url: string | undefined | null;
  interactive?: boolean;
  image: Partial<CardImage> | undefined;
  placeholderImageSize: PlaceholderImageSize;
  onImageLoad?: () => void;
}
/** Right picture is always rectangular */
export const RightPicture: React.FC<React.PropsWithChildren<RightPictureProps>> = ({
  url,
  alt,
  canLock,
  onClick,
  quality,
  interactive = true,
  type,
  image,
  placeholderImageSize,
  onImageLoad,
}) => {
  const { flavour } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  if (mode !== 'edit') {
    // Template images should only NOT show if there is no image or image provider
    if (flavour === CardFlavours.TEMPLATE && !url && !image?.value_provider) {
      return null;
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['right-picture']}
      data-is-locked={image?.is_locked ?? false}
      data-can-lock={canLock}
      data-edit-mode={Boolean(mode === 'edit')}
      data-has-image={!!url}
      data-is-interactive={interactive}
    >
      {!url && (
        <div className={styles['right-picture-edit']}>
          <TemplatePlaceholder
            type={type}
            isOptional={image?.is_optional}
            placeholderImageSize={placeholderImageSize}
            url={url}
            inCardHeader={true}
          />
          {mode === 'edit' && <EditCardImageButton location="right" onClick={onClick} />}
        </div>
      )}
      {url && (
        <>
          <img
            className={styles['right-picture-img']}
            alt={alt}
            src={url}
            data-img-quality={quality}
            onLoad={onImageLoad}
          />
          {mode === 'edit' && !image?.is_locked && <EditCardImageButton location="right" onClick={onClick} />}
        </>
      )}
    </motion.div>
  );
};
